import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
} from 'react'

import withConfig from '../../../wrappers/withConfig'
import { filterModes } from '../box-plot/FilterModeSelector'
import { AppStateContext } from '../AppStateContext'
import { generateDatePostParams } from '../../../../utils/chart/values'
import { toast } from 'react-toastify'
import { ParameterContext } from '../../../wrappers/ParameterContext'
import { APIRequestContext } from '../../../wrappers/APIRequestContext'

const DataContext = createContext(null)

export default withConfig(({ config, children }) => {
    const { API_URL } = config
    const { authenticatedFetch } = useContext(APIRequestContext)
    const {
        mapState: { selectedFeatures },
    } = useContext(AppStateContext)
    const { params } = useContext(ParameterContext)
    const [isCollapsed, setCollapsed] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)
    const [displayFilterModeDropdown, setDisplayFilterModeDropdown] = useState(
        false
    )
    const [piperData, setPiperData] = useState([])
    const [loading, setLoading] = useState(false)
    const [containerDimensions, setContainerDimensions] = useState({
        width: 0,
        height: 0,
    })

    const [filterMode, setFilterMode] = useState(filterModes[0]) //Selected Wells, All Wells

    const includeAllData = useMemo(() => filterMode === 'All Wells', [
        filterMode,
    ])

    const fetchChartData = useCallback(
        (allData, selectedFeatures) => {
            setLoading(true)
            const dates = generateDatePostParams(params)

            const ids = selectedFeatures.map(x => x.get('ClearinghouseNumber'))
            const body = JSON.stringify({
                ClearinghouseNums: ids,
                allData,
                dates,
            })
            authenticatedFetch(`${API_URL}/piper`, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Headers':
                        'Access-Control-Allow-Origin, X-Requested-With, Content-Type, Accept',
                },
                body,
            })
                .then(async response => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then(response => {
                    setPiperData(response.chartData)
                })
                .catch(e => {
                    toast({
                        level: 'error',
                        message:
                            'Box Plot: ' +
                            (e.message
                                ? e.message
                                : 'Unable to connect to the server. Please try again later.'),
                    })
                })
                .finally(() => setLoading(false))
        },
        [authenticatedFetch, setPiperData, setLoading, params]
    )

    //only call fetchChartData if includeAllData is all Wells
    useEffect(() => {
        // if include all data is true then only
        if (includeAllData) {
            setPiperData([])
            fetchChartData(true, [])
        }
    }, [fetchChartData, includeAllData])

    //call fetchChartData when selected features change
    useEffect(() => {
        if (
            filterMode === 'Selected Wells' &&
            selectedFeatures &&
            selectedFeatures.length
        ) {
            setPiperData([])
            fetchChartData(false, selectedFeatures)
        }
    }, [fetchChartData, selectedFeatures, filterMode])

    return (
        <DataContext.Provider
            value={{
                isCollapsed,
                setCollapsed,
                isExpanded,
                setIsExpanded,
                piperData,
                loading,
                filterMode,
                setFilterMode,
                displayFilterModeDropdown,
                setDisplayFilterModeDropdown,
                containerDimensions,
                setContainerDimensions,
            }}
        >
            {children}
        </DataContext.Provider>
    )
})

export { DataContext }
